<template>
  <div class="tab-container">
    <app-spinner v-if="!page_loaded"/>
    <template v-else>
      <app-cells><h2 class="title">Cashflow</h2></app-cells>
      <v-client-table
        :columns="table_cashflow.columns"
        :data="table_cashflow.items"
        :options="table_cashflow.options"
        class="table-default"
        :class="{ 'table-default--loading': table_cashflow_preloader }"
      >
        <template #h__account><div class="table-default__left">Account</div></template>
        <template #h__reference><div class="table-default__left">Reference</div></template>
        <template #h__description><div class="table-default__left">Description</div></template>
        <template #date="{ row }"><span class="table-default__nowrap">{{ dateFormat(row.date) }}</span></template>
        <template #amount="{ row }">
          <span :class="{'table-default__text-green': row.type === 1, 'table-default__text-red': row.type === 2}">
            {{ amountFormat(row.amount) }} {{ row.account.currency.code }}
          </span>
        </template>
        <template #account="{ row }"><div class="table-default__left">{{ row.account.name }}</div></template>
        <template #reference="{ row }"><div class="table-default__left">{{ row.reference || row.id }}</div></template>
        <template #description="{ row }"><div class="table-default__left">{{ row.description }}</div></template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationCashflow"
            v-if="pagination_cashflow.pages > 1"
            v-model="pagination_cashflow.page"
            :page-count="pagination_cashflow.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_cashflow.count" :count="table_cashflow.items.length" :page="pagination_cashflow.page" :limit="10" />
      </app-cells>
      <app-cells>
        <h2 class="title">EFD Receipts</h2>
        <app-input
          v-model="filter_search"
          class="input--300"
          name="search"
          placeholder="Search"
          type="search"
          @input.native="onReceiptSearch"
        />
        <VueCtkDateTimePicker
          id="date_picker_leads_request"
          ref="date_picker"
          v-model="filter_dates"
          :custom-shortcuts="custom_shortcuts"
          button-color="#77b017"
          class="custom-date-time-picker custom-date-time-picker--range"
          color="#77b017"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Date range"
          locale="en"
          no-header
          no-label
          overlay
          range
          @input="onInputDatePicker"
          @is-hidden="onHiddenPicker"
        />
        <app-filters
          v-model="filter_category"
          :filters="filter_category_options"
          name="filterCategory"
          @change="onChangeFilterReceipt"
        />
      </app-cells>
      <v-client-table
        :class="{ 'table-default--loading': table_preloader }"
        :columns="table.columns"
        :data="table.items"
        :options="table.options"
        class="table-default"
      >
        <template #h__verification_code>
          <div class="table-default__left">Verification code</div>
        </template>
        <template #h__supplier>
          <div class="table-default__left">Supplier</div>
        </template>
        <template #receipt_copy="{ row }">
          <a
            :class="{'table-default__disabled': !row.receipt_copy}"
            :href="row.receipt_copy?.file"
            target="_blank"
          >
            <img alt="Attachment" src="@/assets/img/snacks-link.svg">
          </a>
        </template>
        <template #source="{ row }">{{ row.source.name }}</template>
        <template #status="{ row }">
          <div class="table-default__controls">
            <span
              class="table-default__activity table-default__activity--tra"
              :class="{'table-default__activity--active': row.tra_record}"
            />
            <span
              class="table-default__activity table-default__activity--vat"
              :class="{'table-default__activity--active': row.vat_record}"
            />
          </div>
        </template>
        <template #verification_code="{ row }">
          <div class="table-default__left">
            <button
              :class="{'table-default__copy--disabled': !row.verification_link}"
              class="table-default__copy"
              @click="onCopyToClipboard(row.verification_link)"
            >
              {{ row.verification_code }}
            </button>
          </div>
        </template>
        <template #date="{ row }"><span class="table-default__nowrap">{{ dateFormat(row.date) }}</span></template>
        <template #time="{ row }">{{ row.time }}</template>
        <template #amount_total="{ row }">{{ amountFormat(row.amount_total) }}</template>
        <template #amount_vat_exc="{ row }">{{ amountFormat(row.amount_vat_exc) }}</template>
        <template #amount_vat="{ row }">{{ amountFormat(row.amount_vat) }}</template>
        <template #amount_exempted="{ row }">{{ amountFormat(row.amount_exempted) }}</template>
        <template #allocated="{ row }">
          <span class="status-tag" :style="{ 'backgroundColor': parseFloat(row.unallocated) === 0 ? '#77b017' : '#e85c20' }">
            {{ parseFloat(row.unallocated) === 0 ? 'Yes' : 'No' }}
          </span>
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModal(row)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" :limit="10" />
      </app-cells>
      <app-cells>
        <h2 class="title">Invoices</h2>
        <app-filters
          v-model="filter_paid"
          :filters="filter_paid_options"
          name="filterPaid"
          @change="onChangeFilter"
        />
      </app-cells>
      <v-client-table
        :data="table_invoices.items"
        :columns="table_invoices.columns"
        :options="table_invoices.options"
        :class="{ 'table-default--loading': table_invoices_preloader }"
        class="table-default"
      >
        <template #h__invoice_number><div class="table-default__left">Number</div></template>
        <template #date="{ row }">{{ dateFormat(row.date) }}</template>
        <template #amount="{ row }">
          <span
            v-tooltip="{ content: tooltipContent(row), placement: 'top-end', offset: 10, html: true }"
          >
            {{ amountFormat(row.amount_total) }} {{ row.currency.code }}
          </span>
        </template>
        <template #invoice_number="{ row }"><div class="table-default__left">{{ row.invoice_number || '-' }}</div></template>
        <template #payment_plans="{ row }">
          <span
            :style="{ 'backgroundColor': row.payment_status === 'unpaid' ? '#e85c20' : row.payment_status === 'paid' ? '#77b017' : '#439ad1' }"
            class="status-tag"
          >
            {{ row.payment_status === 'unpaid' ? 'Unpaid' : row.payment_status === 'paid' ? 'Paid' : amountFormat(row.paid_amount) }}
          </span>
        </template>
        <template #invoice="{ row }">
          <div class="table-default__controls">
            <a
              :href="row.attachment?.file"
              target="_blank"
              :class="{'table-default__disabled': !row.attachment}"
            >
              <img
                src="@/assets/img/snacks-link.svg"
                alt="Attachment"
              >
            </a>
          </div>
        </template>
        <template #efd="{ row }">
          <span
            :style="{ 'backgroundColor': parseFloat(row.efd_balance) === 0 ? '#77b017' : parseFloat(row.efd_balance) > 0 ? '#e85c20' : '#439ad1'  }"
            class="status-tag"
          >
            {{ parseFloat(row.efd_balance) === 0 ? 'Covered' : amountFormat(row.efd_balance) }}
          </span>
        </template>
        <template #balance="{ row }">
          <span
            :style="{ 'backgroundColor': parseFloat(row.payments_balance) === 0 ? '#77b017' : parseFloat(row.payments_balance) > 0 ? '#e85c20' : '#439ad1'  }"
            class="status-tag"
          >
            {{ parseFloat(row.payments_balance) === 0 ? 'Covered' : amountFormat(row.payments_balance) }}
          </span>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationInvoices"
            v-if="pagination_invoices.pages > 1"
            v-model="pagination_invoices.page"
            :page-count="pagination_invoices.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_invoices.count" :count="table_invoices.items.length" :page="pagination_invoices.page" :limit="10" />
      </app-cells>
    </template>
    <modal
      name="allocate_modal"
      :width="700"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('allocate_modal')" class="popup__close" type="button"></button>
        <div class="popup__title">EFD Allocation</div>
        <div class="popup__body">
          <template v-if="unallocated_amount > 0">
            <div class="popup__subt" style="text-align: left;">
              Unallocated Amount:
              <span :style="{ 'color':unallocated_amount > 0 ? '#e85c20' : '#77b017' }">{{ amountFormat(unallocated_amount) }}</span>
            </div>
            <form @submit.prevent="onCheckForm" :key="repaint_key">
              <app-flex-grid :rows="[[1, '120px', '120px', '130px']]" alignItems="end">
                <template #col-1-1>
                  <app-form-group label="Invoice" class="form-group--no-indent" required>
                    <app-select
                      @search="onInvoiceSearch"
                      v-model="form.supplier_invoice"
                      :options="supplier_invoice_options"
                      :clearable="false"
                      :display-field="option => `${option.invoice_number} (${dateFormat(option.date)})`"
                      label="invoice_number"
                      placeholder="Invoice"
                      :error="$v.form.supplier_invoice.$error"
                    />
                  </app-form-group>
                </template>
                <template #col-1-2>
                  <app-form-group label="Amount (TZS)" class="form-group--no-indent">
                    <app-fin-number
                      v-if="!form.supplier_invoice"
                      readonly
                    />
                    <app-fin-number
                      v-else
                      v-model="form.supplier_invoice.amount_total_tzs"
                      id="amount-tzs"
                      readonly
                    />
                  </app-form-group>
                </template>
                <template #col-1-3>
                  <app-form-group label="Amount" label-for="amount" required class="form-group--no-indent">
                    <app-fin-number
                      v-model="form.amount"
                      id="amount"
                      :error="$v.form.amount.$error"
                    />
                  </app-form-group>
                </template>
                <template #col-1-4>
                  <app-button type="submit" ref="submit">Add</app-button>
                </template>
              </app-flex-grid>
            </form>
          </template>
          <template v-if="receipt_allocations.length">
            <div class="popup__subt" style="text-align: left;">Allocations</div>
            <app-flex-grid
              v-for="allocation in receipt_allocations"
              :key="allocation.id"
              :rows="[[1, '120px', '130px']]"
              alignItems="end"
            >
              <template #col-1-1>
                <app-form-group label="Invoice Number" label-for="invoice-number" class="form-group--no-indent">
                  <app-input
                    v-model="allocation.supplier_invoice.invoice_number"
                    id="invoice-number"
                    readonly
                  />
                </app-form-group>
              </template>
              <template #col-1-2>
                <app-form-group label="Amount" label-for="amount-allocate" class="form-group--no-indent">
                  <app-fin-number
                    v-model="allocation.amount"
                    id="amount-allocate"
                    readonly
                  />
                </app-form-group>
              </template>
              <template #col-1-3>
                <app-button @click.native="onDeleteAllocation(allocation.id)" type="button" ref="delete" theme="error">Delete</app-button>
              </template>
            </app-flex-grid>
          </template>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('allocate_modal')">Save</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import {
  getFinanceSupplierInvoiceList,
  getFinanceSuppliersEFDReceipts,
  getFinanceSuppliersCashflow,
  getFinanceSuppliersEFDAllocations,
  postFinanceSuppliersEFDAllocations,
  deleteFinanceSuppliersEFDAllocations
} from '@/http'
import debounce from 'lodash/debounce'
import {format, parseISO} from 'date-fns'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'SuppliersDetailPurchases',
  components: {VueCtkDateTimePicker},
  data() {
    return {
      page_loaded: false,
      filter_search: '',
      filter_dates: null,
      custom_shortcuts: [
        {key: 'last7Days', label: 'Last 7 days', value: 7},
        {key: 'last30Days', label: 'Last 30 days', value: 30},
        {key: 'last60Days', label: 'Last 60 days', value: 60},
        {key: 'last90Days', label: 'Last 90 days', value: 90},
        {key: 'last180Days', label: 'Last 180 days', value: 180},
        {key: 'thisYear', label: 'This year', value: 'year'},
        {key: 'lastYear', label: 'Last year', value: '-year'},
      ],
      filter_paid: '',
      filter_paid_options: [
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'paid', label: 'Paid' },
        { value: '', label: 'All' },
      ],
      filter_category: '',
      filter_category_options: [
        { value: '', label: 'All' },
        { value: 'unallocated', label: 'Unallocated' },
        { value: 'allocated', label: 'Allocated' },
      ],
      table_preloader: false,
      table: {
        items: [],
        columns: ['receipt_copy', 'source', 'status', 'verification_code', 'date', 'time', 'amount_total', 'amount_vat_exc', 'amount_vat', 'amount_exempted', 'allocated', 'controls'],
        options: {
          headings: {
            'receipt_copy': 'Receipt',
            'source': 'Source',
            'status': 'Status',
            'verification_code': 'Verification code',
            'date': 'Date',
            'time': 'Time',
            'amount_total': 'Total amount',
            'amount_vat_exc': 'VAT Exclude',
            'amount_vat': 'VAT',
            'amount_exempted': 'Exempted',
            'allocated': 'Allocated',
            'controls': '',
          },
          pagination: {show: false},
          sortable: [],
          perPage: 10,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_cashflow_preloader: false,
      table_cashflow: {
        items: [],
        columns: ['account', 'reference', 'date', 'amount', 'description'],
        options: {
          headings: {
            'account': 'Account',
            'reference': 'Reference',
            'date': 'Date',
            'amount': 'Amount',
            'description': 'Description',
          },
          pagination: {show: false},
          sortable: [],
          perPage: 10,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_cashflow: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_invoices_preloader: false,
      table_invoices: {
        items: [],
        columns: [
          'invoice',
          'date',
          'amount',
          'invoice_number',
          'payment_plans',
          'efd',
          'balance',
        ],
        options: {
          headings: {
            'invoice': 'Invoice',
            'date': 'Date',
            'amount': 'Amount',
            'invoice_number': 'Number',
            'payment_plans': 'Payment',
            'efd': 'EFD',
            'balance': 'Balance',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 10,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_invoices: {
        pages: 1,
        page: 1,
        count: 1,
      },
      receipt_to_allocate: null,
      receipt_allocations: [],
      form: {},
      supplier_invoice_options: [],
      repaint_key: 1,
    }
  },
  validations: {
    form: {
      supplier_invoice: { required },
      amount: { required },
    }
  },
  computed: {
    unallocated_amount() {
      const sum = this.receipt_allocations
        .map(item => parseFloat(item.amount) || 0)
        .reduce((a, b) => {
          return a + b
        }, 0)
      return parseFloat(this.receipt_to_allocate?.amount_total) - sum
    }
  },
  created() {
    Promise.all([this.fetchItems(), this.fetchItemsInvoices(), this.fetchItemsCashflow()])
      .then(() => {
        this.page_loaded = true
      })
  },
  methods: {
    async fetchItems() {
      this.table_preloader = true
      const response = await getFinanceSuppliersEFDReceipts({
        page: this.pagination.page,
        page_size: 10,
        ordering: '-timestamp',
        supplier: this.$route.params.id,
        search: this.filter_search,
        from_date: this.filter_search ? '' : this.filter_dates?.start,
        to_date: this.filter_search ? '' : this.filter_dates?.end,
        category: this.filter_search ? '' : this.filter_category
      })
      this.table.items = response.data.results
      this.pagination.pages = response.data.pages
      this.pagination.count = response.data.count
      this.table_preloader = false
    },
    async fetchItemsInvoices() {
      this.table_invoices_preloader = true
      const response = await getFinanceSupplierInvoiceList({
        page: this.pagination_invoices.page,
        page_size: 10,
        supplier: this.$route.params.id,
        payment_status: this.filter_paid,
      })
      this.table_invoices.items = response.data.results
      this.pagination_invoices.pages = response.data.pages
      this.pagination_invoices.count = response.data.count
      this.table_invoices_preloader = false
    },
    async fetchItemsCashflow() {
      this.table_cashflow_preloader = true
      const response = await getFinanceSuppliersCashflow(this.$route.params.id, {
        page: this.pagination_cashflow.page,
        page_size: 10,
      })
      this.table_cashflow.items = response.data.results
      this.pagination_cashflow.pages = response.data.pages
      this.pagination_cashflow.count = response.data.count
      this.table_cashflow_preloader = false
    },
    async fetchAllocations() {
      const response = await getFinanceSuppliersEFDAllocations({efd_receipt: this.receipt_to_allocate.id})
      this.receipt_allocations = response.data
    },
    onChangeFilter() {
      this.pagination_invoices.page = 1
      this.fetchItemsInvoices()
    },
    onChangeFilterReceipt() {
      this.pagination.page = 1
      this.fetchItems()
    },
    onInputDatePicker() {
      if (!this.$refs.date_picker.pickerOpen) {
        this.fetchItems()
      }
    },
    onHiddenPicker() {
      if (this.filter_dates?.start) {
        this.fetchItems()
      }
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onClickPaginationInvoices(page) {
      this.pagination_invoices.page = page
      this.fetchItemsInvoices()
    },
    onClickPaginationCashflow(page) {
      this.pagination_cashflow.page = page
      this.fetchItemsCashflow()
    },
    async onShowModal(row) {
      this.receipt_to_allocate = row
      await this.fetchAllocations()
      this.form = {}
      this.$v.form.$reset()
      this.$modal.show('allocate_modal')
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      postFinanceSuppliersEFDAllocations({...this.form, supplier_invoice: this.form.supplier_invoice.id, supplier_efd_receipt: this.receipt_to_allocate.id})
        .then(() => {
          this.fetchAllocations()
            .then(() => {
              if (this.unallocated_amount <= 0) this.fetchItems()
            })
          this.form = {}
          this.$v.form.$reset()
          this.repaint_key += 1
          this.$refs.submit.preload = false
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    onDeleteAllocation(id) {
      this.$refs.delete.preload = true
      const unallocated = this.unallocated_amount
      deleteFinanceSuppliersEFDAllocations(id)
        .then(() => {
          this.fetchAllocations()
            .then(() => {
              if (unallocated <= 0) this.fetchItems()
            })
          this.$refs.delete.preload = false
        })
        .catch(() => { this.$refs.delete.preload = false })
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    amountFormat(amount) {
      return amount ? new Intl.NumberFormat('ru-RU').format(amount) : '-'
    },
    tooltipContent(row) {
      if (row.currency.code !== 'USD') return `BOT EXCHANGE:<br>USD: ${this.amountFormat(row.amount_total_usd)}`
      if (row.currency.code !== 'TZS') return `BOT EXCHANGE:<br>TZS: ${this.amountFormat(row.amount_total_tzs)}`
    },
    onCopyToClipboard(string) {
      navigator.clipboard.writeText(string)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => {
          console.error('Something went wrong')
        })
    },
    onReceiptSearch() {
      this.searchReceipt(this)
    },
    searchReceipt: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
    onInvoiceSearch(search, loading) {
      loading(true)
      this.searchInvoice(loading, search, this)
    },
    searchInvoice: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.supplier_invoice_options = []
      } else {
        getFinanceSupplierInvoiceList({search, efd_covered: false, supplier: vm.$route.params.id})
          .then(response => {
            vm.supplier_invoice_options = response.data
            loading(false)
          })
      }
    }, 500),
  }
}
</script>
